import React, { useState, useEffect, useRef } from "react";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import Checkbox from "@mui/material/Checkbox";

const MultiSelectDropdown = ({ id, options, value, onChange, onClose, placeholder, disabled, searchable }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
        if (onClose) onClose();
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open, onClose]);

  const filtered = searchable && search
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const allSelected = options.length > 0 && options.every((o) => value.includes(o.value));
  const someSelected = !allSelected && options.some((o) => value.includes(o.value));

  const handleSelectAll = () => onChange(allSelected ? [] : options.map((o) => o.value));

  const handleToggle = (val) =>
    onChange(value.includes(val) ? value.filter((v) => v !== val) : [...value, val]);

  const displayText = value.length > 0 ? `${value.length} selected` : placeholder;

  return (
    <div ref={containerRef} style={{ position: "relative", width: "220px" }}>
      <button
        id={id}
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        style={{
          width: "100%",
          height: "36px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 10px",
          border: "1px solid #ccc",
          borderRadius: "5px",
          background: disabled ? "#f5f5f5" : "#fff",
          cursor: disabled ? "not-allowed" : "pointer",
          fontSize: "16px",
          color: value.length === 0 ? "#888" : "#333",
          outline: "none",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {displayText}
        </span>
        <ExpandMoreIcon
          style={{
            fontSize: "18px",
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 9999,
            background: "#fff",
            border: "1px solid #ccc",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            width: "100%",
            maxHeight: "200px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {searchable && (
            <div style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
              <input
                autoFocus
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                style={{
                  width: "100%",
                  padding: "4px 8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                }}
              />
            </div>
          )}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {!search && (
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderBottom: "1px solid #eee",
                  fontWeight: 600,
                  fontSize: "16px",
                  userSelect: "none",
                }}
              >
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleSelectAll}
                  size="small"
                />
                Select All
              </label>
            )}
            {filtered.map((o) => (
              <label
                key={o.value}
                title={o.tooltip || undefined}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "6px 12px",
                  cursor: "pointer",
                  userSelect: "none",
                  fontSize: "16px",
                }}
              >
                <Checkbox
                  checked={value.includes(o.value)}
                  onChange={() => handleToggle(o.value)}
                  size="small"
                />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {o.label}
                </span>
              </label>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: "12px", color: "#888", fontSize: "16px" }}>No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
